import { createFileRoute } from '@tanstack/react-router';
import { Activity } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export const Route = createFileRoute('/_authed/event-catalog')({
  component: EventCatalogPage,
});

type EventDef = {
  type: string;
  description: string;
  status_change?: string;
  source: string[];
  webhooks: 'always' | 'on_match' | 'never';
};

const EVENTS: EventDef[] = [
  {
    type: 'initiated',
    description: 'A payment row was created. Always the first event.',
    status_change: '— → pending',
    source: ['api'],
    webhooks: 'on_match',
  },
  {
    type: 'otp_required',
    description: 'PSP returned an OTP challenge that the customer must complete (Sadad).',
    status_change: 'pending → requires_action',
    source: ['api'],
    webhooks: 'on_match',
  },
  {
    type: 'otp_submitted',
    description: 'Customer (or hosted-checkout page) submitted the OTP code.',
    status_change: 'requires_action → processing',
    source: ['api'],
    webhooks: 'on_match',
  },
  {
    type: 'redirected',
    description: 'PSP requires a hosted-redirect flow (Moamalat LightBox / T-Lync).',
    status_change: 'pending → requires_action',
    source: ['api'],
    webhooks: 'on_match',
  },
  {
    type: 'qr_issued',
    description: 'Mobile-wallet QR was generated for the customer to scan (Mobicash).',
    status_change: 'pending → requires_action',
    source: ['api'],
    webhooks: 'on_match',
  },
  {
    type: 'lightbox_issued',
    description: 'Moamalat LightBox iframe parameters were issued.',
    status_change: 'pending → requires_action',
    source: ['api'],
    webhooks: 'on_match',
  },
  {
    type: 'gateway_ack',
    description: 'PSP acknowledged the request and reported a non-terminal status.',
    source: ['api', 'poll'],
    webhooks: 'on_match',
  },
  {
    type: 'gateway_rejected',
    description: 'PSP rejected the request (declined / invalid params / etc).',
    status_change: '* → failed',
    source: ['api', 'webhook'],
    webhooks: 'on_match',
  },
  {
    type: 'webhook_received',
    description: 'An inbound PSP webhook was received and matched to this payment.',
    source: ['webhook'],
    webhooks: 'never',
  },
  {
    type: 'polled',
    description: 'Sadad poller checked the PSP for a status update.',
    source: ['poll'],
    webhooks: 'never',
  },
  {
    type: 'succeeded',
    description: 'Payment completed successfully. Funds will settle per the PSP schedule.',
    status_change: '* → succeeded',
    source: ['api', 'webhook', 'poll'],
    webhooks: 'always',
  },
  {
    type: 'failed',
    description: 'Payment will not complete. Reason in the event payload.',
    status_change: '* → failed',
    source: ['api', 'webhook', 'poll'],
    webhooks: 'always',
  },
  {
    type: 'expired',
    description: 'Payment expired before reaching a terminal state.',
    status_change: '* → expired',
    source: ['poll', 'system'],
    webhooks: 'always',
  },
  {
    type: 'refunded',
    description: 'A refund was recorded against a previously-succeeded payment.',
    status_change: 'succeeded → refunded',
    source: ['admin'],
    webhooks: 'always',
  },
  {
    type: 'replayed',
    description: 'An operator replayed a webhook delivery from the admin UI.',
    source: ['admin'],
    webhooks: 'never',
  },
];

function EventCatalogPage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <header>
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-2xl font-semibold tracking-tight">{t('event_catalog.title')}</h1>
        </div>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          {t('event_catalog.subtitle')}
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        {EVENTS.map((e) => (
          <Card key={e.type}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-2">
                <code className="font-mono text-sm">{e.type}</code>
                <WebhooksBadge mode={e.webhooks} />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-muted-foreground">{e.description}</p>
              {e.status_change && (
                <div className="text-xs">
                  <span className="text-muted-foreground">{t('event_catalog.status_change')}: </span>
                  <code className="font-mono">{e.status_change}</code>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-1 text-xs">
                <span className="text-muted-foreground">{t('event_catalog.source')}: </span>
                {e.source.map((s) => (
                  <Badge key={s} variant="secondary" className="text-[10px]">
                    {s}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function WebhooksBadge({ mode }: { mode: EventDef['webhooks'] }) {
  const { t } = useTranslation();
  if (mode === 'always') return <Badge variant="success">{t('event_catalog.always_emits')}</Badge>;
  if (mode === 'on_match')
    return <Badge variant="outline">{t('event_catalog.on_match')}</Badge>;
  return <Badge variant="secondary">{t('event_catalog.never_emits')}</Badge>;
}
